import { useState, useEffect, useRef, useMemo } from 'react';
import { container, TYPES } from "../../inversifyconfig/container.config";
import { BuildPath } from '../../helpers/Paths';

//interfaces
import { IunImage, Status, Notification, NotificationStatus, SideImageProps, SearchResultsProps, SearchBoxProps } from '../../interfaces/unsplash';
import { IHttpRequest } from  '@/interfaces/IHttp';

//components
import SideImage from '../elements/unsplash/SideImage';
import NotificationBar from '../elements/unsplash/NotificationBar';
import SearchResults from '../elements/unsplash/SearchResults';
import ResultLabel from '../elements/unsplash/ResultLabel';
import SearchBox from '../elements/unsplash/SearchBox';

import '../../css/unsplash.scss';



const Unsplash = () => {
    const [isModal, setIsModal] = useState<boolean>(false);
    const [isPageView, setIsPageView] = useState<boolean>(false);
    const [isListView, setIsListView] = useState<boolean>(false);
    const [isFeaturedImgView, setIsFeaturedImgView] = useState<boolean>(false);
    const [isWidgetFeaturedImgView, setIsWidgetFeaturedImgView] = useState<boolean>(false);
    const [isFormBuilder, setIsFormBuilder] = useState<boolean>(false);
    const [keyword, setKeyword] = useState("");
    const [page, setPage] = useState(1);
    const [images, setImages] = useState<IunImage[]>([]);
    const [savedImages, setSavedImages] = useState<IunImage[]>([]);
    const [status, setStatus] = useState<Status>(Status.inital);
    const [bigImage, setBigImage] = useState<IunImage | null>(null);
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
    const [notification, setnotification] = useState<Notification>({ notificationStatus: null, show: false });
    const [btnSaved, setBtnSaved] = useState<boolean>(false);
    const searchResults = useRef(null);
    const [insertClicked, setInsertClicked] = useState<boolean>(false);

    // On load
    useEffect(() => {
        search(true);

        // Is this modal version of unsplash?   
        document.querySelector('#medialibrarymodal') ? setIsModal(true) : setIsModal(false);
        document.querySelector('#contentList') ||
        document.querySelector('#sortableListItems') || 
        document.querySelector('#react-main-menu-portal') ||
        document.querySelector('#react-list-portal')
        ? setIsListView(true) : setIsListView(false);
        document.querySelector(".pageeditor-add") ? setIsPageView(true) : setIsPageView(false);

        // Featured image tab in knockout, use global store to detect
        (window as any).globalStore?.getStatus() === "featuredImage" ? setIsFeaturedImgView(true) : setIsFeaturedImgView(false);   
        (window as any).globalStore?.getStatus() === "widgetFeaturedImage" ? setIsWidgetFeaturedImgView(true) : setIsWidgetFeaturedImgView(false);
        (window as any).globalStore?.getStatus() === "formBuilder" ? setIsFormBuilder(true) : setIsFormBuilder(false);

    }, [])

    // Page updated (infinate scroll)  
    useEffect(() => {
        if (page > 1) {
            search(false);
        }

    }, [page])

    // Detect if single char in textbox to allow/dissallow submit
    useEffect(() => {

        if (keyword.length >= 1) {
            setSubmitDisabled(false)
        }
        else {
            setSubmitDisabled(true)
        }

    }, [keyword]);

    const search = (clear: boolean): void => { 
        setStatus(Status.searching);

        if (clear && searchResults.current) {
            ;(searchResults.current as HTMLElement).scrollTop = 0;
            setBigImage(null);
        }

        let httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);

        httpCall.get(buildUrl()).then((x) => {

            // New search
            if (clear) {
                setImages(x.data.images);
            }
            // Append
            else {
                filterDuplicates(x.data.images);
            }

            setStatus(Status.finished);
        })
        .catch(() => {
            setStatus(Status.error);
        });

    }

    const filterDuplicates = (nextImages: IunImage[]): void => {

        if (nextImages === null) {
            return;
        }

        const filtered: IunImage[] = [];

        nextImages.forEach(x => {

            const exists = images.filter(z => z.id === x.id).length;

            if (!exists) {
                filtered.push(x);
            }

        });

        let combined = images.concat(filtered);

        setImages(combined);
    }

    const buildUrl = (): string => {

        let unSplashUrl = `/unsplash/search?appguid=${getAppGuid()}&page=${page}`;

        if (keyword !== "") {
            unSplashUrl += `&keyword=${keyword}`;
        }

        return unSplashUrl;
    }

    const searchBtnClick = (): void => {
        // Clear all
        setImages([]);

        // Search
        search(true);
    }

    const onScroll = (e: any): void => {

        const bottom = e.target.scrollHeight - Math.floor(e.target.scrollTop) <= e.target.clientHeight + 50;

        const bottom2 = e.target.scrollHeight - Math.ceil(e.target.scrollTop) <= e.target.clientHeight + 50;

        if ((bottom || bottom2) && status === Status.finished) {
            setPage(page + 1);
        }

    }

    const buildMediaLink = (): string => {

        const url = window.location.pathname.split('/');

        if (url.length > 3 && url[2] !== "") {
            return `/a/${url[2]}/Media`
        }

        return "";
    }

    const getAppGuid = (): string => {

        let path = window.location.pathname;

        if (process.env.NODE_ENV === "development") {
            path = "/a/99bb9979-9c5c-4d33-832c-12d94d7ed528/Unsplash"
        }

        const appGuid = path.split('/')[2];

        if (appGuid !== null && appGuid !== "") {
            return appGuid;
        }

        return "";

    }

    const save = (): void => {

        setInsertClicked(true)
        
        const appGuid = getAppGuid();

        if (appGuid === "") {
            setnotification({ notificationStatus: NotificationStatus.error, show: true })
            console.log("Missing app guid")
            return;
        }

        let httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);

        let folderId = localStorage.getItem('folderId')

        folderId = folderId && folderId !== 'undefined'  ? `&folderId=${folderId}` : ''

        httpCall.get(`/unsplash/save?id=${bigImage?.id}&appguid=${appGuid}&filename=${bigImage?.filename}${folderId}`).then((response:any) => {

            // If modal view add to content list, page or featured image
            if (isModal){
                if (isFormBuilder){
                    addToFormBuilder(response.data)
                    return;
                }
                else if (isWidgetFeaturedImgView){
                    addToFeaturedImageViaWidget(response.data);
                    return;
                }
                else if (isFeaturedImgView){
                    addToFeaturedImage(response.data);
                    return;
                }
                else if (isListView){
                    addToListFromModal(response.data.id);
                    return;
                }              
                else if (isPageView){
                    addToPageFromModal(response.data)
                    return;
                }                  
            }

            setnotification({ notificationStatus: NotificationStatus.success, show: true })
            setBtnSaved(true);
           
            const newImages = [...savedImages];
            
            if(bigImage) {
                  newImages.push(bigImage);
            }
          
            setSavedImages(newImages);

            setTimeout(() => {
                setnotification({ notificationStatus: null, show: false });
                setBtnSaved(false)
            }, 3000);

        })
        .catch(() => {
            setnotification({ notificationStatus: NotificationStatus.error, show: true })
        })
        .finally(() => {
            ;(window as any)?.functions?.closeWidgetPanel?.()
        });    
    }

    const resetSearch = () => {
        search(true);
    }

    const exit = () : void => {

        //e.preventDefault();

        window.localStorage.setItem('unsplash', 'true')
      
        if (isModal){
            // Unhide footer
            const footer = document.querySelector('#medialibrarymodal .modal-footer');

            if (footer) {
                (footer as HTMLInputElement).style.display = "block";
            }

            // Switch tab
            const mediaLibref: HTMLElement = document.getElementById("mediaLibrary") as HTMLElement;
            const unsplashlibref: HTMLElement = document.getElementById("unsplashContainerTab") as HTMLElement;
    
            unsplashlibref.style.display = "none";
            mediaLibref.style.display = "block";                  
        }
        else{
            // Non modal
            window.location.replace(buildMediaLink());
        }
    }

    const addToListFromModal = (id:number) : void => {

        let parentId;

        if (document.querySelector("#Id")){
            parentId = parseInt((document.querySelector("#Id") as HTMLInputElement).value)
        }

        // For sub list
        const path = window.location.pathname.split('Index/');
        if (path.length === 2){
            parentId = parseInt(path[1])
        }

        let httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);

        const addToList = {
            appguid: getAppGuid(),
            id: id,
            parentid: parentId
        }



        httpCall.post(`/unsplash/InsertMediaContentToListViaModal`, addToList)
        .then(async () => {
            // Reload window - same as current media library
            await (window as any).functions?.refreshMainMenu?.() 
        }).catch(() => {
            console.log("Failed")
        }).finally(() => {
            closeModal();
        })

    }

    const addToPageFromModal = (asset:any): void => {

        // Call global function to add though knockout
        const item:any = {
            title: asset.filename,
            assetId : asset.id,
            url : asset.location
        };

        (window as any).selectItemFromUnsplash(item);
    }

    const addToFeaturedImage = (asset:any): void => {

        // Call global function to add though knockout
        const item:any = {
            backgroundImgUrl: asset.location,
            id : asset.id
        };

        (window as any).insertFeaturedImageViaUnsplash(item);
    }

    const addToFeaturedImageViaWidget = (asset:any):void => {

        // Call global function to add though knockout
        const item:any = {
            backgroundImgUrl: asset.location,
            id : asset.id
        };

        (window as any).reactPageEditorSync.addFeaturedImageWidgetViaUnsplash(item);
    }

    const addToFormBuilder = (asset:any):void => {
        console.log(asset);
        const item:any = {
            url: asset.location,
            id : asset.id,
            title: asset.filename
        };
        const itemSelected = new CustomEvent("itemSelectedFromMediaLibrary", {
            detail: item,
        });
        // Call global function to add though knockout
        document.dispatchEvent(itemSelected);
        closeModal();
    }
    
    // Modal view
    const closeModal = ():void => {
        (window as any).closeMediaLibraryModal();
    }

    const insertDisabled = ():boolean => {
        
        if (insertClicked){
            return true;
        }

        return bigImage === null;


    }

    const sideImageProps: SideImageProps = {
        bigImage: bigImage as IunImage,
        status: status,
        count: images.length,
        save: save,
        btnSaved: btnSaved,
        savedImages: savedImages,
        isModal: isModal
    }

    const searchResultProps: SearchResultsProps = {
        ref: searchResults,
        onScroll: onScroll,
        images: images,
        setBigImage: setBigImage,
        bigImage: bigImage as IunImage,
        status: status,
        keyword: keyword
    }

    const searchBoxProps: SearchBoxProps = {
        submitDisabled: submitDisabled,
        searchBtnClick: searchBtnClick,
        onChange: setKeyword,
        resetSearch : resetSearch,
        keyword : keyword
    }

    return (
        <div className={isModal ? 'unsplash-container unsplash-media-modal' : 'unsplash-container' }>

            {!isModal && 
                <NotificationBar show={notification.show} notificationStatus={notification.notificationStatus} />
            }

            <div className="title-bar unsplash-title">
                <h2>Media Library</h2>
            </div>

            <div className="exit" onClick={exit} >
                <a>
                    <img src={`${BuildPath()}/images/unsplash/BackIcon.svg`} alt=""/> Exit Unsplash
                </a>
            </div>

            <div className="unsplash-search-box">

                <SearchBox data={searchBoxProps} />

                {useMemo(() => <ResultLabel keyword={keyword} status={status} resultCount={images.length} />, [status, images])}

            </div>

            <div className="result-container">
           
                <SearchResults data={searchResultProps} />
               
                <SideImage data={sideImageProps} />

            </div>

            {isModal && 
                <div className="modal-footer-unsplash">
                    <button className="insert" disabled={insertDisabled()}  onClick={save}>Insert Selected Media</button>
                    <button className="cancel" onClick={closeModal}>Cancel</button>
                </div>
            }

        </div>
    );
};

export default Unsplash;


